import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { EImageVariant } from '@onesource/schemas';
import { useCreateMediaFileMutation, useGetMediaFilesQuery } from '@onesource/services';
import type { ProcessServerConfigFunction } from 'filepond';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { MdGridView, MdList } from 'react-icons/md';

import { FILE_FORMATS } from '../file-uploader/file-uploader';
import { useGetByDevice } from '../hooks';
import { MediaGrid } from './media-grid';
import { MediaTable } from './media-table';
import { MediaUploaderDialog } from './media-uploader-dialog';
import type { TUploadMeta } from './process-handler';

type TView = 'grid' | 'table';

export type TMediaLibraryPageProps = {
  /** Title shown in the sticky header */
  title?: string;
  /** localStorage key for persisting view preference */
  viewPrefKey?: string;
  /**
   * Factory that builds a process handler for the given enabled variants.
   * Use this (instead of `processHandler`) to enable the image variant checkboxes.
   */
  processHandlerFactory?: (enabledVariants: EImageVariant[]) => ProcessServerConfigFunction;
  /** Direct process handler — use when variant selection is not needed. */
  processHandler?: ProcessServerConfigFunction;
  /**
   * Variants listed in the upload dialog. With `processHandler` these are display-only, so they
   * must match the variants that handler uploads or the dialog advertises sizes it never makes.
   */
  imageVariants?: EImageVariant[];
  /** Whether variant checkboxes are interactive (admin) or read-only (user). Defaults to false. */
  variantsEditable?: boolean;
  /**
   * When true, shows a toggle to switch between My Uploads / System / All file views.
   * The API enforces that non-admin users cannot access system or all-files views.
   */
  showScopeToggle?: boolean;
  /**
   * When true, shows a System / Personal toggle inside the upload dialog.
   * Only pass this for admin / kit_organizer users — the API also enforces this server-side.
   */
  showSystemToggle?: boolean;
};

function getSavedView(key: string): TView {
  try {
    const saved = localStorage.getItem(key);
    if (saved === 'grid' || saved === 'table') {
      return saved;
    }
  } catch {
    // ignore
  }
  return 'grid';
}

function saveView(key: string, view: TView) {
  try {
    localStorage.setItem(key, view);
  } catch {
    // ignore
  }
}

type TFileScope = 'personal' | 'system' | 'all';

export function MediaLibraryPage(params: TMediaLibraryPageProps) {
  const {
    title = 'Media Library',
    viewPrefKey = 'media-library-view',
    processHandler,
    processHandlerFactory,
    imageVariants,
    variantsEditable,
    showScopeToggle,
    showSystemToggle,
  } = params;
  const [view, setView] = useState<TView>(() => getSavedView(viewPrefKey));
  const [page, setPage] = useState(1);
  const [scope, setScope] = useState<TFileScope>('personal');
  const { isMobile } = useGetByDevice();

  const isSystemParam: boolean | 'all' | undefined = showScopeToggle
    ? scope === 'all'
      ? 'all'
      : scope === 'system'
    : undefined;

  const { data, isLoading } = useGetMediaFilesQuery({ page, limit: 24, isSystem: isSystemParam });
  const files = data?.data ?? [];
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  const { mutate: createMediaFile } = useCreateMediaFileMutation();

  const handleUploaded = useCallback(
    (metaJson: string, isSystem: boolean) => {
      try {
        const meta: TUploadMeta = JSON.parse(metaJson);
        createMediaFile(
          {
            originalName: meta.originalName,
            cdnUrls: meta.cdnUrls,
            mimeType: meta.mimeType,
            sizeBytes: meta.sizeBytes,
            isSystem,
          },
          {
            onError: error => {
              // Surface server-side quota errors (e.g. monthly personal upload limit exceeded).
              const message =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
                'Failed to save file record';
              enqueueSnackbar(message, { variant: 'error' });
            },
          },
        );
      } catch {
        enqueueSnackbar('Failed to save file record', { variant: 'error' });
      }
    },
    [createMediaFile],
  );

  const handleViewChange = useCallback(
    (next: TView) => {
      setView(next);
      saveView(viewPrefKey, next);
    },
    [viewPrefKey],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sticky header */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          px: 3,
          pt: 3,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="h6">{title}</Typography>
            {data && (
              <Typography variant="body2" color="text.secondary">
                ({data.total})
              </Typography>
            )}
          </Box>

          {showScopeToggle && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(['personal', 'system', 'all'] as TFileScope[]).map(s => (
                <IconButton
                  key={s}
                  size="small"
                  color={scope === s ? 'primary' : 'default'}
                  onClick={() => {
                    setScope(s);
                    setPage(1);
                  }}
                  sx={theme => ({
                    fontSize: { xs: 11, sm: 13 },
                    fontWeight: 600,
                    borderRadius: 1,
                    px: 1,
                    bgcolor: scope === s ? `${theme.palette.primary.main}14` : 'transparent',
                  })}
                >
                  {s === 'personal' ? 'My Uploads' : s === 'system' ? 'System' : 'All'}
                </IconButton>
              ))}
            </Box>
          )}

          {showScopeToggle && <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />}

          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Grid view" arrow>
              <IconButton
                size="small"
                color={view === 'grid' ? 'primary' : 'default'}
                onClick={() => handleViewChange('grid')}
              >
                <MdGridView size={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="List view" arrow>
              <IconButton
                size="small"
                color={view === 'table' ? 'primary' : 'default'}
                onClick={() => handleViewChange('table')}
              >
                <MdList size={20} />
              </IconButton>
            </Tooltip>
          </Box>

          {!isMobile && (
            <MediaUploaderDialog
              allowedFormats={FILE_FORMATS.IMAGE_AND_PDF}
              processHandlerFactory={processHandlerFactory}
              processHandler={processHandler}
              imageVariants={imageVariants}
              onUploaded={handleUploaded}
              variantsEditable={variantsEditable}
              showSystemToggle={showSystemToggle}
            />
          )}
        </Box>
        {isMobile && (
          <MediaUploaderDialog
            allowedFormats={FILE_FORMATS.IMAGE_AND_PDF}
            processHandlerFactory={processHandlerFactory}
            processHandler={processHandler}
            imageVariants={imageVariants}
            onUploaded={handleUploaded}
            variantsEditable={variantsEditable}
            showSystemToggle={showSystemToggle}
          />
        )}
      </Box>

      {/* Scrollable file grid / table */}
      <Box
        sx={{
          px: 3,
          pb: 2,
          overflowY: 'auto',
          height: 'calc(100dvh - 220px)',
        }}
      >
        {view === 'grid' ? (
          <MediaGrid files={files} isLoading={isLoading} />
        ) : (
          <MediaTable files={files} isLoading={isLoading} />
        )}
      </Box>

      {/* Sticky pagination footer */}
      {totalPages > 1 && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            bgcolor: 'background.default',
            px: 3,
            py: 1.5,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Box>
  );
}
